import { makeStyles } from '@map/theme';
import {
  FormControl,
  FormHelperText,
  InputBase,
  InputLabelProps,
  NativeSelect,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { REQUIRED_FIELD_MARKER } from '@shared/appConstants';
import { useFormatMessage } from '@shared/hooks/intl';
import useErrorMessage from '@shared/hooks/useErrorMessage';
import { styled } from '@shared/theme';
import ChevronDownIcon from '@ui/Icon/library/ChevronDownIcon';
import { PropsWithoutRef, ReactElement, ReactNode, Ref, forwardRef, useId } from 'react';

const useStyles = makeStyles(({ palette, spacing, transitions }) => ({
  root: {
    display: 'flex',
    // adds text-align: right when in RTL mode
    'html[dir="rtl"] & input': {
      '&[dir="ltr"], &[dir="auto"]': {
        // jss swaps this to right when in rtl mode
        textAlign: 'left',
      },
    },
    '& input[type="date"]::-webkit-inner-spin-button, & input[type="date"]::-webkit-calendar-picker-indicator':
      {
        display: 'none',
        WebkitAppearance: 'none',
      },
  },
  control: {
    transition: transitions.create('border-color', {
      duration: transitions.duration.shorter,
      easing: transitions.easing.easeOut,
    }),
    'label + &': {
      marginTop: 0,
    },
  },
  requiredMarker: {
    '&::after': {
      content: `"${REQUIRED_FIELD_MARKER}"`,
    },
  },
  input: {
    '&::placeholder': {
      color: 'inherit',
      opacity: 0.5,
    },
  },
  fakePlaceholder: {
    color: palette.text.disabled,
  },
  label: {
    marginBottom: spacing(2),
  },
  errorLabel: {
    color: palette.error.main,
  },
  helperText: {
    margin: spacing(2, 0, 0),
  },
  borderless: {
    borderColor: 'transparent',
  },
  subtitle: {
    marginTop: spacing(1),
  },
  'margin-none': {},
  'margin-normal': { margin: spacing(0, 0, 5) },
  'margin-dense': { margin: spacing(0, 0, 2) },
}));

const filterInputLabelProps = (props: Partial<InputLabelProps>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { variant, ref, shrink, ...resultProps } = props;
  return resultProps;
};

const SelectIcon = styled(ChevronDownIcon)(({ spacing }) => ({
  marginRight: spacing(1.25),
}));

// this is only used for interoperabilty with 3rd-party libraries,
// not all props of the TextField are supported
type InheritedProps = Omit<PropsWithoutRef<TextFieldProps>, 'inputRef'>;

type Option = { value: number | string; label: string | ReactElement; disabled?: boolean };

type Props = InheritedProps & {
  label?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  errorMessageId?: string;
  placeholderIntlKey?: string;
  helperText?: ReactNode;
  description?: ReactNode;
  characters?: 'auto' | 'special';
  borderless?: boolean;
  value?: string;
  options?: Option[];
};
const TextFormField: FFC<Props> = (props, ref) => {
  const {
    id,
    className,
    label,
    placeholderIntlKey,
    errorMessageId,
    helperText,
    borderless,
    InputLabelProps,
    InputProps,
    inputProps,
    options,
    description,
    characters = 'auto',
    margin = 'normal',
    error: propError,
    ...childProps
  } = props;

  const controlId = useId();
  const t = useFormatMessage();
  const { classes, cx } = useStyles();
  const errorMessage = useErrorMessage(errorMessageId);
  const helper = propError !== false && errorMessageId ? errorMessage : helperText;
  const error = propError || !!errorMessageId;
  const controlProps = {
    id: id || controlId,
    error,
    ...childProps,
    placeholder: placeholderIntlKey ? t(placeholderIntlKey, undefined, '') : childProps.placeholder,
    ...InputProps,
    inputProps: {
      ...inputProps,
      dir: characters === 'special' ? 'ltr' : 'auto',
      className: cx(classes.input, inputProps?.className),
    },
  };
  return (
    <FormControl
      className={cx(classes.root, classes[`margin-${margin}` as const], className)}
      hiddenLabel
      ref={ref}
    >
      {(label || description) && (
        <div className={classes.label}>
          {label && (
            <Typography
              variant="subtitle1"
              component="label"
              htmlFor={controlProps.id}
              gutterBottom
              className={cx(error && classes.errorLabel, InputLabelProps?.className)}
              {...(InputLabelProps ? filterInputLabelProps(InputLabelProps) : null)}
            >
              {label}
              {props.required && <span aria-hidden="true" className={classes.requiredMarker} />}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" color="textSecondary" className={classes.subtitle}>
              {description}
            </Typography>
          )}
        </div>
      )}

      {options ? (
        <NativeSelect
          {...(controlProps as any)}
          className={cx(
            classes.control,
            !controlProps.value && controlProps.placeholder && classes.fakePlaceholder,
            InputProps?.className,
          )}
          IconComponent={SelectIcon}
        >
          {!controlProps.value && controlProps.placeholder && (
            <option key="none" value="">
              {controlProps.placeholder}
            </option>
          )}
          {options.map(({ value, label, disabled }) => (
            <option key={value} value={value} disabled={disabled}>
              {label}
            </option>
          ))}
        </NativeSelect>
      ) : (
        <InputBase
          {...(controlProps as any)}
          className={cx(classes.control, borderless && classes.borderless, InputProps?.className)}
        />
      )}
      {helper && (
        <FormHelperText error={error} className={classes.helperText}>
          {helper}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default forwardRef(TextFormField);
